import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MobileMenu from "../components/MobileMenu";
import Overlay from "../components/Overlay";
import Footer from "../components/Footer";
import Datepicker from "../components/JalaliDatePicker";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import api from "../services/api";
import Toast from "../components/toast";
import dayjs, { Dayjs } from "dayjs";
import jalali from "jalali-plugin-dayjs";

dayjs.extend(jalali);

export default function UserInfo() {
    const { isAuthenticated, accessToken, loading: loadingauth } = useAuth();
    const navigate = useNavigate();

    const [fullname, setFullname] = useState("");
    const [phone, setPhone] = useState("");
    const [nationalid, setNationalid] = useState("");
    const [birth_date, setBirth_date] = useState<Dayjs | null>();

    const [level, setLevel] = useState("");
    const [profileImage, setProfileImage] = useState<string>();

    const [menuOpen, setMenuOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("profile");

    const [toast, setToast] = useState<{ title: string; text: string }>();
    const [toastshow, setToastshow] = useState(false);

    const convertToDayjs = (dateStr?: string): dayjs.Dayjs | null => {
        if (!dateStr) return null;

        try {
            return dayjs(dateStr);
        } catch (e) {
            console.error("Date conversion error:", e);
            return null;
        }
    };

    const fetchdata = async () => {
        try {
            const profileRes = await api.get("/profile", {
                headers: {
                    Authorization: accessToken ? `Bearer ${accessToken}` : "",
                },
            });

            if (profileRes.data?.data) {
                const data = profileRes.data.data;

                setFullname(data.full_name || "");
                setPhone(data.phone || "");
                setNationalid(data.national_id || "");
                setLevel(data.level || "");
                setProfileImage(data.avatar);

                if (data.birth_date) {
                    setBirth_date(convertToDayjs(data.birth_date));
                }
            }
        } catch (err) {
            console.log("data fetch error:", err);
        }
    };

    useEffect(() => {
        if (loadingauth) return;
        if (!isAuthenticated) {
            navigate('/');
            return;
        }
        fetchdata();
    }, [isAuthenticated, loadingauth, accessToken]);

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!fullname.trim()) {
            setToastshow(true);
            setToast({ title: 'خطا', text: "نام و نام خانوادگی خالی است." });
            return;
        }

        try {
            const payload = {
                full_name: fullname.trim(),
                national_id: nationalid.trim() || undefined,
                birth_date: birth_date?.format('YYYY/MM/DD') || undefined,
            };

            const profileRes = await api.put("/profile", payload, {
                headers: { Authorization: accessToken ? `Bearer ${accessToken}` : "" },
            });

            if (profileRes.data.success || profileRes.data.status === "success") {
                setToastshow(true);
                setToast({ title: 'موفقیت', text: "اطلاعات با موفقیت ثبت شد." });
            }
        } catch (err: any) {
            console.error(err);
            setToastshow(true);
            setToast({ title: 'خطا', text: "خطا در ثبت اطلاعات" });
        }
    }

    function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            setToastshow(true);
            setToast({ title: 'خطا', text: "فقط فایل تصویری انتخاب کنید" });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            setToastshow(true);
            setToast({ title: 'خطا', text: "حجم عکس نباید بیشتر از 10 مگابایت باشد" });
            return;
        }

        uploadAvatar(file);
    }

    const uploadAvatar = async (selectedFile: File) => {
        const formData = new FormData();
        formData.append("avatar", selectedFile);

        try {
            const response = await api.post("/profile/avatar", formData, {
                headers: {
                    Authorization: accessToken ? `Bearer ${accessToken}` : "",
                    "Content-Type": "multipart/form-data",
                },
            });

            if (response.data.status === 'success' || response.data.success) {
                setToastshow(true);
                setToast({ title: 'موفقیت', text: "آواتار با موفقیت آپلود شد" });
                setTimeout(() => navigate(0), 1500);
            }
        } catch (error) {
            setToastshow(true);
            setToast({ title: 'خطا', text: "خطا در آپلود تصویر" });
        }
    };

    return (
        <div className="min-h-dvh bg-darkbg">
            <div
                dir="rtl"
                className="w-full max-w-[450px] mx-auto flex flex-col min-h-dvh max-h-dvh relative overflow-hidden font-yekan text-white"
            >
                <Overlay active={menuOpen} close={() => setMenuOpen(false)} />
                <MobileMenu active={menuOpen} />
                <Header menuOpen={menuOpen} close={() => setMenuOpen(!menuOpen)} />

                <section className="mt-6 px-4">
                    <div className="flex items-start justify-between gap-4 w-full">
                        <h1 className="text-primary text-[28px] font-bold mt-6 whitespace-nowrap">
                            اطلاعات کاربری
                        </h1>

                        <div className="flex flex-col items-center gap-2 shrink-0">
                            <div className="relative w-[110px] h-[110px]">
                                <img
                                    src={profileImage || "./assets/img/avatar.png"}
                                    alt="profile"
                                    className="w-full h-full rounded-full border-[5px] border-primary object-cover"
                                />
                                <label className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#1f1f1f] 
                                w-[40px] h-[40px] rounded-full flex items-center justify-center cursor-pointer">
                                    <input
                                        type="file"
                                        hidden
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                    />
                                    <svg width="24" height="24" stroke="currentColor" className="text-primary" strokeWidth="2.2" fill="none">
                                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3l1.5-3h6L15 7h6a2 2 0 0 1 2 2z" />
                                        <circle cx="12" cy="14" r="4" />
                                    </svg>
                                </label>
                            </div>
                        </div>
                    </div>
                </section>

                <main className="flex-1 px-4 overflow-y-auto scrollbar-thin pb-[74px]">
                    <div className="space-y-5">
                        <div>
                            <label className="text-lg font-bold text-primary block mb-2">
                                شماره :
                            </label>
                            <input
                                type="text"
                                value={phone}
                                disabled
                                className="w-full h-[50px] rounded-xl px-4 text-right bg-gradient-to-br from-[#272727] to-[#252424] text-gray-400 outline-none border border-transparent"
                            />
                        </div>

                        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                            <div>
                                <label className="text-lg font-bold text-primary block mb-2">
                                    نام و نام خانوادگی:
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={fullname}
                                    onChange={(e) => setFullname(e.target.value)}
                                    className="w-full h-[50px] rounded-xl px-4 text-right bg-gradient-to-br from-[#3a3a3a] to-[#2c2c2c] text-white outline-none border border-transparent focus:border-primary/40"
                                />
                            </div>

                            <div>
                                <label className="text-lg font-bold text-primary block mb-2">
                                    کد ملی :
                                </label>
                                <input
                                    type="text"
                                    value={nationalid}
                                    onChange={(e) => setNationalid(e.target.value)}
                                    className="w-full h-[50px] rounded-xl px-4 text-right bg-gradient-to-br
                                     from-[#3a3a3a] to-[#2c2c2c] text-white outline-none border border-transparent focus:border-primary/40"
                                />
                            </div>

                            <div>
                                <label className="text-lg font-bold text-primary block mb-2">
                                    تاریخ تولد :
                                </label>
                                <Datepicker
                                    value={birth_date}
                                    onChange={(value) => setBirth_date(value)}

                                    placeholder="تاریخ تولد را انتخاب کنید"
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full h-[50px] bg-primary text-black font-bold mt-6 rounded-2xl active:bg-primary/90 transition"
                            >
                                ثبت اطلاعات
                            </button>
                        </form>
                    </div>
                </main>

                <Toast
                    active={toastshow}
                    title={toast?.title}
                    text={toast?.text}
                    setToastshow={setToastshow}
                />
                <Footer activeTab={activeTab} setActiveTab={setActiveTab} />
            </div>
        </div>
    );
}